/**
 * Inhalt der Detailspalte und des Detailblatts (Board „Verlauf“): Stufen, Fakten, Kasten mit
 * Rückmeldung oder Herleitung und die technischen Referenzen – für alle Ansichten gleich.
 */
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import type { HistoryDetail } from './historyDetailModel';
import { TONE_CLASS } from './HistoryPill';

const LINE_TONE = {
  green: 'text-forest-deep',
  amber: 'text-amber-ink',
  red: 'text-sealwax',
  grey: 'text-ink',
};

export function HistoryDetailView({
  detail,
  surface,
  children,
}: {
  detail: HistoryDetail;
  /** Fläche der Kästen: weiß in der Spalte, Papier im weißen Blatt. */
  surface: string;
  children?: ReactNode;
}) {
  return (
    <>
      {detail.steps && (
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Stufen">
          {detail.steps.map((step) => (
            <span
              key={step.label}
              className={cn(
                'rounded-pill inline-flex h-[22px] items-center px-2.5 text-[11px] font-bold',
                TONE_CLASS[step.tone],
              )}
            >
              {step.label}
            </span>
          ))}
        </div>
      )}
      <dl className={cn('rounded-lg px-3.5 py-1', surface)}>
        {detail.facts.map(([label, value]) => (
          <div
            key={label}
            className="border-paper flex min-h-[34px] items-center gap-2.5 border-b py-1.5 text-[13px] last:border-b-0"
          >
            <dt className="text-ink-soft grow">{label}</dt>
            <dd className="break-words text-right font-bold">{value}</dd>
          </div>
        ))}
      </dl>
      {detail.box && (
        <section className={cn('flex flex-col gap-1.5 rounded-lg p-3.5', surface)}>
          <h3 className="text-[13px] font-bold">{detail.box.title}</h3>
          {detail.box.lines.map((line) => (
            <p
              key={`${line.code}-${line.text}`}
              className={cn('text-[13px]', LINE_TONE[line.tone])}
            >
              {line.text}{' '}
              {line.code && (
                <code className="text-ink-soft break-all font-mono text-[11px]">{line.code}</code>
              )}
            </p>
          ))}
        </section>
      )}
      {children}
      <details className={cn('rounded-lg px-3.5', surface)}>
        <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-bold">
          Technische Referenzen
        </summary>
        <dl className="text-ink-soft flex flex-col gap-1 pb-3 font-mono text-[11px]">
          {detail.refs.map(([label, value]) => (
            <div key={label} className="flex flex-wrap gap-x-2">
              <dt>{label}</dt>
              <dd className="break-all">{value}</dd>
            </div>
          ))}
        </dl>
      </details>
    </>
  );
}
